import React, { useEffect, useState } from 'react';

// DM-only composer for campaign announcements ("table notes"). Phase II /
// Phase C (2026-05-17). Opened from the `+ Compose` button or the edit
// affordance on an existing note in the campaign detail view.
//
// Modes:
//   `note=null`  → create a new announcement; onSubmit receives {title, body, pinned}
//   `note={...}` → edit the given announcement; onSubmit receives the same
//                  shape plus an `announcementId` identifier
//
// Field caps mirror the server-side normalizer in announcements.ts:
// title ≤ 120, body ≤ 2000.

const MAX_TITLE = 120;
const MAX_BODY = 2_000;

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '0.92rem',
  fontFamily: theme.typography?.fontFamily || 'serif',
  boxSizing: 'border-box',
});

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.78rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

const helperStyle = (theme) => ({
  marginTop: 4,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.74rem',
  color: theme.colors.textMuted,
});

export const AnnouncementComposerModal = ({
  theme,
  campaign,
  note,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!note?.id;

  useEffect(() => {
    setTitle(note?.title || '');
    setBody(note?.body || '');
    setPinned(!!note?.pinned);
    setError(null);
  }, [note?.id, note?.title, note?.body, note?.pinned]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!onSubmit) return;
    setSaving(true);
    setError(null);
    try {
      if (!title.trim()) throw new Error('Title is required');
      await onSubmit({
        announcementId: note?.id,
        title,
        body,
        pinned,
      });
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !note?.id) return;
    // Cheap confirm; a dedicated dialog can come in Session 11 polish.
    if (typeof window !== 'undefined' && !window.confirm('Delete this announcement?')) return;
    setSaving(true);
    setError(null);
    try {
      await onDelete(note.id);
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label={isEdit ? 'Edit announcement' : 'Compose announcement'}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 560,
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
          <h2 style={{
            margin: 0,
            fontFamily: t.headingFamily,
            fontSize: '1.3rem',
            color: c.accent,
          }}>{isEdit ? 'Edit Announcement' : 'New Announcement'}</h2>
          <span style={{ color: c.textMuted, fontSize: '0.86rem' }}>{campaign?.name}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle(theme)} htmlFor="acm-title">Title</label>
            <input
              id="acm-title"
              type="text"
              value={title}
              maxLength={MAX_TITLE}
              placeholder="Tonight's session moved to 8:30pm"
              onChange={(e) => setTitle(e.target.value)}
              style={fieldStyle(theme)}
              autoFocus
            />
            <div style={helperStyle(theme)}>
              {title.length} / {MAX_TITLE}
            </div>
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="acm-body">Message</label>
            <textarea
              id="acm-body"
              value={body}
              maxLength={MAX_BODY}
              placeholder="Anything you want every member to see when they open the dashboard."
              rows={5}
              onChange={(e) => setBody(e.target.value)}
              style={{ ...fieldStyle(theme), resize: 'vertical', minHeight: 120 }}
            />
            <div style={helperStyle(theme)}>
              {body.length} / {MAX_BODY}
            </div>
          </div>

          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            cursor: 'pointer',
            fontFamily: t.fontFamily,
            color: c.textSecondary,
            fontSize: '0.88rem',
          }}>
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Pin to top of the campaign's announcement list</span>
          </label>
        </div>

        {error && (
          <div style={{
            marginTop: 16,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.86rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 22 }}>
          <div>
            {isEdit && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                style={{
                  padding: '9px 14px',
                  background: 'rgba(212,106,90,0.15)',
                  color: c.danger || '#d46a5a',
                  border: `1px solid ${c.danger || '#d46a5a'}`,
                  borderRadius: 4,
                  cursor: saving ? 'wait' : 'pointer',
                  fontFamily: t.fontFamily,
                  fontSize: '0.84rem',
                }}
              >Delete</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '9px 16px',
                background: 'transparent',
                color: c.textSecondary,
                border: `1px solid ${c.borderLight}`,
                borderRadius: 4,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: t.fontFamily,
                fontSize: '0.88rem',
              }}
            >Cancel</button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              style={{
                padding: '9px 18px',
                background: c.accent,
                color: c.textOnAccent || '#000',
                border: 'none',
                borderRadius: 4,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: t.fontFamily,
                fontWeight: 600,
                fontSize: '0.9rem',
                opacity: saving || !title.trim() ? 0.7 : 1,
              }}
            >{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Post')}</button>
          </div>
        </div>
      </form>
    </div>
  );
};
